// Страница профиля студента: просмотр и редактирование персональных данных (возраст, уровень, цель обучения).

import { useEffect, useState } from 'react';
import { Container, Card, Form, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import AppNavbar from '../../components/common/AppNavbar';
import { getStudentProfile, saveStudentProfile } from '../../api/studentApi';

const AGE_OPTIONS = ['до 18', '18–25', '26–35', '36–45', '46+'];
const LEVEL_OPTIONS = ['A1', 'A2', 'B1', 'B2', 'C1'];
const GOAL_OPTIONS = [
  { value: 'travel', label: 'Путешествия' },
  { value: 'work', label: 'Работа / карьера' },
  { value: 'study', label: 'Учёба' },
  { value: 'relocation', label: 'Переезд за рубеж' },
  { value: 'hobby', label: 'Хобби / саморазвитие' },
];

const LEVEL_COLORS = { A1: 'secondary', A2: 'info', B1: 'primary', B2: 'warning', C1: 'success' };

export default function StudentProfilePage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ age_range: '', english_level: '', learning_goal: '' });

  useEffect(() => {
    getStudentProfile()
      .then(({ data }) => {
        if (data?.profile) {
          setForm({
            age_range: data.profile.age_range || '',
            english_level: data.profile.english_level || '',
            learning_goal: data.profile.learning_goal || '',
          });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.age_range || !form.english_level || !form.learning_goal) {
      setError('Пожалуйста, заполните все поля.');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess(false);
    try {
      await saveStudentProfile(form);
      localStorage.removeItem('ewl_onboarding_skipped');
      setSuccess(true);
    } catch {
      setError('Не удалось сохранить профиль. Попробуйте ещё раз.');
    } finally {
      setSaving(false);
    }
  };

  const select = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  if (loading) {
    return (
      <>
        <AppNavbar />
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 56px)' }}>
          <Spinner animation="border" variant="primary" />
        </div>
      </>
    );
  }

  return (
    <>
      <AppNavbar />
      <div style={{ background: 'var(--ewl-bg-body)', minHeight: 'calc(100vh - 56px)' }}>
        <Container className="py-4" style={{ maxWidth: 600 }}>
          <h5 className="fw-bold mb-4" style={{ color: '#1e2a4a', letterSpacing: '-0.01em' }}>
            Мой профиль
          </h5>

          <Card className="border-0" style={{ borderRadius: '1rem', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <Card.Body className="p-4">
              <Form onSubmit={handleSave}>

                {/* Возраст */}
                <Form.Group className="mb-4">
                  <Form.Label className="fw-semibold" style={{ color: '#1e2a4a', fontSize: '0.9rem' }}>
                    Возраст
                  </Form.Label>
                  <div className="d-flex flex-wrap gap-2 mt-1">
                    {AGE_OPTIONS.map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => select('age_range', opt)}
                        style={{
                          padding: '0.4rem 1rem',
                          borderRadius: '2rem',
                          border: form.age_range === opt ? '2px solid var(--bs-primary)' : '1.5px solid #dee2e6',
                          background: form.age_range === opt ? 'rgba(79,110,247,0.08)' : '#fff',
                          color: form.age_range === opt ? 'var(--bs-primary)' : '#495057',
                          fontWeight: form.age_range === opt ? 600 : 400,
                          fontSize: '0.875rem',
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                        }}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </Form.Group>

                {/* Уровень */}
                <Form.Group className="mb-4">
                  <Form.Label className="fw-semibold" style={{ color: '#1e2a4a', fontSize: '0.9rem' }}>
                    Уровень английского
                  </Form.Label>
                  <div className="d-flex flex-wrap gap-2 mt-1">
                    {LEVEL_OPTIONS.map((lvl) => (
                      <button
                        key={lvl}
                        type="button"
                        onClick={() => select('english_level', lvl)}
                        style={{
                          padding: '0.4rem 1.2rem',
                          borderRadius: '2rem',
                          border: form.english_level === lvl ? '2px solid var(--bs-primary)' : '1.5px solid #dee2e6',
                          background: form.english_level === lvl ? 'rgba(79,110,247,0.08)' : '#fff',
                          color: form.english_level === lvl ? 'var(--bs-primary)' : '#495057',
                          fontWeight: form.english_level === lvl ? 700 : 400,
                          fontSize: '0.9rem',
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                        }}
                      >
                        {lvl}
                        {form.english_level === lvl && (
                          <Badge
                            bg={LEVEL_COLORS[lvl] || 'secondary'}
                            className="ms-1"
                            style={{ fontSize: '0.65rem', verticalAlign: 'middle' }}
                          >
                            выбран
                          </Badge>
                        )}
                      </button>
                    ))}
                  </div>
                </Form.Group>

                {/* Цель */}
                <Form.Group className="mb-4">
                  <Form.Label className="fw-semibold" style={{ color: '#1e2a4a', fontSize: '0.9rem' }}>
                    Цель изучения
                  </Form.Label>
                  <div className="d-flex flex-column gap-2 mt-1">
                    {GOAL_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => select('learning_goal', opt.value)}
                        style={{
                          padding: '0.6rem 1rem',
                          borderRadius: '0.5rem',
                          border: form.learning_goal === opt.value ? '2px solid var(--bs-primary)' : '1.5px solid #dee2e6',
                          background: form.learning_goal === opt.value ? 'rgba(79,110,247,0.08)' : '#fff',
                          color: form.learning_goal === opt.value ? 'var(--bs-primary)' : '#495057',
                          fontWeight: form.learning_goal === opt.value ? 600 : 400,
                          fontSize: '0.9rem',
                          textAlign: 'left',
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                        }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </Form.Group>

                {error && (
                  <Alert variant="danger" style={{ borderRadius: '0.5rem', fontSize: '0.875rem' }}>
                    {error}
                  </Alert>
                )}
                {success && (
                  <Alert variant="success" style={{ borderRadius: '0.5rem', fontSize: '0.875rem' }}>
                    Профиль успешно сохранён.
                  </Alert>
                )}

                <Button
                  type="submit"
                  disabled={saving}
                  style={{
                    background: 'linear-gradient(135deg, #4f6ef7 0%, #7c3aed 100%)',
                    border: 'none',
                    borderRadius: '0.5rem',
                    fontWeight: 600,
                    width: '100%',
                    padding: '0.65rem',
                  }}
                >
                  {saving ? <Spinner animation="border" size="sm" className="me-2" /> : null}
                  Сохранить профиль
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Container>
      </div>
    </>
  );
}
